import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material'
import { Archive } from '@mui/icons-material'

import { useDownloadArchivePageMutation } from '@/apis/opsApi'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { useProjectId, useProjectToday } from '@/hooks/useReportArgs'
import { downloadText } from '@/utils/download'
import { shiftDate } from '@/utils/dates'

/** Pages fetched before the browser asks to narrow the range (≈400 archive files each). */
const MAX_PAGES = 50

/**
 * Raw archive export, any age: every accepted event as NDJSON, straight from the archive
 * that is kept for the life of the project (retention only applies to reports).
 */
export function ArchiveExportCard() {
    const projectId = useProjectId()
    const today = useProjectToday()
    const dispatch = useAppDispatch()
    const [from, setFrom] = useState(shiftDate(today, -6))
    const [to, setTo] = useState(today)
    const [progress, setProgress] = useState<{ pages: number; files: number } | null>(null)
    const [fetchPage] = useDownloadArchivePageMutation()

    const run = async () => {
        const chunks: string[] = []
        let cursor: string | null = null
        let files = 0
        setProgress({ pages: 0, files: 0 })
        try {
            for (let page = 1; page <= MAX_PAGES; page++) {
                const r = await fetchPage({ projectId, from, to, cursor }).unwrap()
                chunks.push(r.text)
                files += r.files
                setProgress({ pages: page, files })
                cursor = r.next
                if (!cursor) {
                    break
                }
            }
            if (cursor) {
                dispatch(
                    showNotification({
                        message: `Large range: saved the first ${MAX_PAGES} pages. Narrow the range (or use the API's cursor) for the rest.`,
                        severity: 'warning',
                    }),
                )
            }
            const text = chunks.join('')
            if (text.length === 0) {
                dispatch(
                    showNotification({
                        message: 'No events archived in this range.',
                        severity: 'info',
                    }),
                )
            } else {
                downloadText(`archive-${from}-${to}.ndjson`, text, 'application/x-ndjson')
            }
        } catch (err) {
            dispatch(
                showNotification({
                    message: v2ErrorMessage(err) ?? 'Export failed',
                    severity: 'error',
                }),
            )
        } finally {
            setProgress(null)
        }
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Archive fontSize="small" color="action" />
                    <Typography variant="subtitle1">Raw event archive</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Every event YOM accepted, one JSON object per line, for any date range. The
                    archive is kept for the life of the project; report retention does not apply to
                    it. Dates are the UTC day the event was received.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        type="date"
                        label="From"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <Button
                        variant="outlined"
                        disabled={progress !== null || !from || !to || from > to}
                        onClick={() => {
                            void run()
                        }}
                    >
                        Download NDJSON
                    </Button>
                </Box>
                {progress && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress />
                        <Typography variant="caption" color="text.secondary">
                            {progress.files.toLocaleString('en-US')} archive files read
                        </Typography>
                    </Box>
                )}
                <Alert severity="info" sx={{ mt: 2 }}>
                    For large ranges or automation use{' '}
                    <code>GET /v2/projects/:id/archive/export?from=…&amp;to=…</code> and follow the{' '}
                    <code>X-YOM-Next-Cursor</code> header, or set up a warehouse export below.
                </Alert>
            </CardContent>
        </Card>
    )
}
